/**
 * MyCertificates — Dashboard-embedded viewer listing every certificate the
 * learner has earned. Each tile links to the individual certificate view
 * page and exposes the LinkedIn add-to-profile URL directly.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, ArrowUpRight, GraduationCap, Loader2, Linkedin, ShieldCheck,
  Printer, ExternalLink, Award,
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import ToolSelector from '../components/ToolSelector';

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (_) { return iso; }
};

const linkedInHref = (cert, verifyUrl) => {
  const issued = new Date(cert.issued_at);
  const p = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: cert.course_title,
    organizationName: cert.academy_director || 'Freeze Dry Academy',
    issueYear: String(issued.getFullYear()),
    issueMonth: String(issued.getMonth() + 1),
    certUrl: verifyUrl,
    certId: cert.cert_number,
  });
  return `https://www.linkedin.com/profile/add?${p.toString()}`;
};

const CertRow = ({ cert }) => {
  const navigate = useNavigate();
  const verifyUrl = `${window.location.origin}/certificate/${cert.cert_number}`;
  return (
    <div
      className="pro-card p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-primary/40 transition-colors"
      data-testid={`my-cert-row-${cert.cert_number}`}
    >
      <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        <GraduationCap className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">
          Certificate of Completion
        </div>
        <h3 className="font-heading text-lg font-extrabold text-foreground tracking-tight truncate">
          {cert.course_title}
        </h3>
        <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-foreground/60">
          <span className="font-mono font-bold text-foreground/80">{cert.cert_number}</span>
          <span>·</span>
          <span>Issued {fmt(cert.issued_at)}</span>
          {cert.total_time_minutes ? (<><span>·</span><span>{cert.total_time_minutes} min tracked</span></>) : null}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/certificate/${cert.cert_number}`)}
          className="rounded-full font-bold"
          data-testid={`my-cert-view-${cert.cert_number}`}
        >
          View <ArrowUpRight className="w-3 h-3 ml-1" />
        </Button>
        <a
          href={linkedInHref(cert, verifyUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full font-bold px-3 h-8 text-xs bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
          data-testid={`my-cert-linkedin-${cert.cert_number}`}
        >
          <Linkedin className="w-3 h-3 mr-1" /> Add to LinkedIn
        </a>
      </div>
    </div>
  );
};

const MyCertificates = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = window.localStorage.getItem('token');
        const res = await axios.get(`${API}/lms/my-certificates`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setCerts(res.data?.certificates || []);
      } finally { setLoading(false); }
    })();
  }, [API, user]);

  const grouped = useMemo(() => {
    const y = {};
    certs.forEach((c) => {
      const year = new Date(c.issued_at).getFullYear();
      (y[year] = y[year] || []).push(c);
    });
    return Object.entries(y).sort((a, b) => b[0].localeCompare(a[0]));
  }, [certs]);

  return (
    <div className="min-h-screen bg-background" data-testid="my-certificates-page">
      <ToolSelector />

      <section className="bg-navy-spotlight text-background">
        <div className="container mx-auto px-6 py-12">
          <div className="eyebrow text-amber-brand">Your certificates</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">
            {certs.length === 0 ? (
              <>No certificates <span className="text-amber-brand">yet.</span></>
            ) : certs.length === 1 ? (
              <>1 certificate on your <span className="text-amber-brand">wall.</span></>
            ) : (
              <><span className="text-amber-brand">{certs.length}</span> certificates on your wall.</>
            )}
          </h1>
          <p className="mt-3 text-background/70 max-w-2xl">
            Every credential below is HMAC-signed and independently verifiable via its public URL. Print, share, or import to LinkedIn.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/coursework')}
              className="rounded-full bg-background/5 border-background/20 text-background hover:bg-background/10 font-bold"
              data-testid="my-certs-transcript-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" /> View coursework transcript
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/learning')}
              className="rounded-full bg-background/5 border-background/20 text-background hover:bg-background/10 font-bold"
              data-testid="my-certs-learning-btn"
            >
              <GraduationCap className="w-4 h-4 mr-1.5" /> Continue learning
            </Button>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        {loading && (
          <div className="text-center py-16 text-foreground/60" data-testid="my-certs-loading">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Loading certificates…
          </div>
        )}
        {!loading && certs.length === 0 && (
          <div className="pro-card p-10 text-center" data-testid="my-certs-empty">
            <Award className="w-10 h-10 text-foreground/30 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-extrabold text-foreground tracking-tight">
              Nothing to display here yet.
            </h2>
            <p className="mt-3 text-foreground/70">
              Complete any course in the Learning library — pass its quiz gates and clear the dwell-time anti-cheat — and your first signed certificate will land here automatically.
            </p>
            <Button
              onClick={() => navigate('/learning')}
              className="mt-6 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="my-certs-empty-cta"
            >
              Browse courses <ArrowUpRight className="ml-1.5 w-4 h-4" />
            </Button>
          </div>
        )}
        {!loading && grouped.map(([year, items]) => (
          <section key={year} className="mb-10" data-testid={`my-certs-year-${year}`}>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-2xl font-extrabold text-foreground tracking-tight">{year}</h2>
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-foreground/60 font-bold">{items.length} earned</span>
            </div>
            <div className="space-y-3">
              {items.map((c) => <CertRow key={c.cert_number} cert={c} />)}
            </div>
          </section>
        ))}
        {!loading && certs.length > 0 && (
          <div className="pro-card p-5 mt-4 flex items-start gap-4 bg-primary/5 border-primary/20" data-testid="my-certs-verify-info">
            <ExternalLink className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground/70">
              <span className="font-extrabold text-foreground">Anyone can verify these credentials</span> by visiting <span className="font-mono text-primary">/certificate/{'{'}certificate-number{'}'}</span>. The verification is signature-checked in constant time and revoke-aware.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyCertificates;
